import type { FC } from "react";

import type { IViewportWrapperRequiredProps } from "@/src/master/ViewportInjector";
import ViewportWrapper from "@/src/master/ViewportInjector";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Loading } from "@Components/Loading";
import { getHeaderHeight, scrollToY } from "@Components/Modules/Helpers";

import type { IShoppingMallTabBrandsFilter } from "./ShoppingMallTabBrandsFilter";
import { ShoppingMallTabBrandsFilter } from "./ShoppingMallTabBrandsFilter";
import type { IShoppingMallTabBrandsItem } from "./ShoppingMallTabBrandsItem";
import { ShoppingMallTabBrandsItem } from "./ShoppingMallTabBrandsItem";

import * as css from "./ShoppingMallTabBrands.module.css";

export interface IShoppingMallTabBrands extends IComponentAjax<IShoppingMallTabBrandsItem[]> {
    title: string;
    filters: IShoppingMallTabBrandsFilter;
    shopText: string;
    offersText: string;
}

interface IShoppingMallTabBrandsState extends IComponentAjaxState {
    selectedCategory: string | null;
    brandItems: IShoppingMallTabBrandsItem[];
}

class ShoppingMallTabBrandsBase extends ComponentAjax<
    IShoppingMallTabBrandsItem[],
    IShoppingMallTabBrands & IViewportWrapperRequiredProps,
    IShoppingMallTabBrandsState
> {
    public static readonly defaultProps: IShoppingMallTabBrands = {
        title: null,
        filters: null,
        shopText: null,
        offersText: null,
    };

    constructor(props: IShoppingMallTabBrands & IViewportWrapperRequiredProps) {
        super(props);

        this.state = {
            selectedCategory: null,
            isLoading: !(props.initialData && props.initialData.length > 0),
            brandItems: props.initialData ? props.initialData.slice() : [],
        };
    }

    public componentDidMount() {
        if (this.state.isLoading === false) {
            return;
        }

        this.fetchList();
    }

    public fetchList() {
        this.setState({ isLoading: true });

        this.ajaxRequest({ category: this.state.selectedCategory });
    }

    public render() {
        const categoryChange = (category: string) => {
            this.setState({ selectedCategory: category }, () => this.fetchList());
        };

        const anchorClick = (anchor: string) => {
            const element = document.getElementById(`brand-${anchor}`);
            if (!element) {
                return;
            }

            const windowScroll =
                document.documentElement.scrollTop - (document.documentElement.clientTop || 0);

            const rect = element.getBoundingClientRect();

            const headerHeight = getHeaderHeight();

            scrollToY(windowScroll + rect.top - headerHeight);
        };

        let filters = null;

        if (this.props.filters) {
            filters = { ...this.props.filters };
            filters.selectedCategory = this.state.selectedCategory;
            filters.onFilterChange = categoryChange;
            filters.onAnchorClick = anchorClick;
            filters.groupKeys = this.state.brandItems.map((item) => item.groupKey);
        }

        const renderBrands = () => {
            if (this.state.isLoading === true) {
                return null;
            }

            if (!this.state.brandItems || this.state.brandItems.length === 0) {
                return null;
            }

            const renderTitles = () => {
                if (!this.state.brandItems || this.state.brandItems.length === 0) {
                    return null;
                }

                return (
                    <div className={css.brandsTitle}>
                        <div className={css.shop}>{this.props.shopText}</div>
                        {!this.props.viewport.isMobile && (
                            <div className={css.offers}>{this.props.offersText}</div>
                        )}
                    </div>
                );
            };

            const anchors: string[] = [];

            return (
                <div className={css.brands}>
                    {renderTitles()}
                    {this.state.brandItems.map((item, i) => {
                        const anchorAdded = anchors.indexOf(item.groupKey) > -1;
                        let id;
                        if (!anchorAdded) {
                            id = `brand-${item.groupKey}`;
                            anchors.push(item.groupKey);
                        }

                        return (
                            <div key={i} id={id} className={css.brandItem}>
                                <ShoppingMallTabBrandsItem {...item} />
                            </div>
                        );
                    })}
                </div>
            );
        };

        return (
            <section {...this.rootDefaultAttributes(css.shoppingMallTabBrandsComponent)}>
                <h2>{this.props.title}</h2>
                <ShoppingMallTabBrandsFilter {...filters} />
                {this.state.isLoading && <Loading />}
                {renderBrands()}
                {this.renderAjaxErrorContainer()}
            </section>
        );
    }

    protected ajaxSuccess(response: IShoppingMallTabBrandsItem[]): void {
        if (!response || response.length === 0) {
            this.setState({ isLoading: false, brandItems: [] });
            return;
        }

        this.setState({ isLoading: false, brandItems: response });
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false, brandItems: [] });
        return true;
    }
}

export const ShoppingMallTabBrands: FC<IShoppingMallTabBrands> = (props) => (
    <ViewportWrapper Component={ShoppingMallTabBrandsBase} props={props} />
);
